import { useCallback, useEffect, useMemo, useState } from 'react';
import { OctagonAlert, Search } from 'lucide-react';
import { DEFECT_REPORTERS, STATUS_LABEL, formatDay, todayUk } from '@nu/shared';
import { api } from '../../api/client.js';
import Pager, { pageCount, pageSlice } from '../../components/Pager.jsx';
import { useAutoRefresh } from '../../useAutoRefresh.js';
import { AddDefect } from './Defects.jsx';
import { useBookingEditor, useBookingRefs } from './shared.jsx';
import { problem } from './util.js';

// Operations > Defects: every completed install that has stopped working,
// open ones first, with how long each has been open and where its return
// visit has got to. Click one to open the job (mark it fixed there). Add
// defect finds a completed job and raises one.
const REPORTED_BY = Object.fromEntries(DEFECT_REPORTERS.map((r) => [r.value, r.label]));
const daysBetween = (a, b) => Math.round((new Date(`${b}T12:00:00Z`) - new Date(`${a}T12:00:00Z`)) / 86_400_000);

export default function DefectsPage({ me }) {
  const canEdit = me.capabilities.includes('page.defects.edit');
  const [status, setStatus] = useState('open'); // open | fixed | '' (all)
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [reporter, setReporter] = useState('');
  const [team, setTeam] = useState('');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);

  const load = useCallback(() => api(`/bookings/defects${status ? `?status=${status}` : ''}`).then((r) => { setList(r.defects); setError(null); }, (e) => setError(problem(e))), [status]);
  useEffect(() => { setList(null); load(); }, [load]);
  useAutoRefresh(load);
  useEffect(() => { setPage(1); }, [status, reporter, team, q, size]);

  const { types, teams } = useBookingRefs();
  const { notice, setNotice, openBooking, modal } = useBookingEditor({ canEdit, types, teams, reload: load });

  const today = todayUk();
  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (list || []).filter((d) => (!reporter || d.reportedBy === reporter)
      && (!team || (d.job.teamName || '') === team)
      && (!term || [d.job.reference, d.job.address, d.job.postcode, d.job.clientRef, d.job.customerName, d.description].some((v) => String(v || '').toLowerCase().includes(term))));
  }, [list, reporter, team, q]);
  const teamNames = useMemo(() => [...new Set((list || []).map((d) => d.job.teamName).filter(Boolean))].sort(), [list]);

  // Totals over what's loaded (open, or fixed, or both).
  const open = (list || []).filter((d) => d.status === 'open');
  const fixed = (list || []).filter((d) => d.status === 'fixed');
  const overWeek = open.filter((d) => daysBetween(d.reportedOn, today) > 7).length;
  const avgFix = fixed.length ? Math.round(fixed.reduce((n, d) => n + daysBetween(d.reportedOn, d.fixedOn), 0) / fixed.length) : null;

  const onPage = Math.min(page, pageCount(shown.length, size));
  const visible = pageSlice(shown, onPage, size);

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Defects</h1>
            <p className="muted">Completed installs that have stopped working, so we go back. Each stays open until it&apos;s marked fixed on the job. Click one to open the job.</p>
          </div>
          {canEdit && <div className="head-actions"><button type="button" className="btn primary with-icon" onClick={() => setAdding(true)}><OctagonAlert size={18} aria-hidden="true" /> Add defect</button></div>}
        </div>
        {notice && <p className={`notice-line notice-${notice.tone}`} role="status">{notice.text}</p>}
        <div className="filters">
          <div className="view-toggle" role="group" aria-label="Status">
            {[['open', 'Open'], ['fixed', 'Fixed'], ['', 'All']].map(([v, l]) => (
              <button key={l} type="button" className={`btn${status === v ? ' active' : ''}`} aria-pressed={status === v} onClick={() => setStatus(v)}>{l}</button>
            ))}
          </div>
          <label className="defect-search filter-search">
            <Search size={16} aria-hidden="true" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="NU number, address, postcode, job ref, customer or what's wrong" aria-label="Search defects" />
          </label>
          <select value={reporter} onChange={(e) => setReporter(e.target.value)} aria-label="Reported by">
            <option value="">Anyone reported</option>
            {DEFECT_REPORTERS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <select value={team} onChange={(e) => setTeam(e.target.value)} aria-label="Team that installed">
            <option value="">All teams</option>
            {teamNames.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {list && (
          <div className="stat-row where-stats">
            {status !== 'fixed' && <div className="stat"><span className="stat-num">{open.length}</span><span className="stat-label">Open</span></div>}
            {status !== 'fixed' && <div className={`stat${overWeek ? ' stat-warn' : ''}`}><span className="stat-num">{overWeek}</span><span className="stat-label">Open over a week</span></div>}
            {status !== 'open' && <div className="stat"><span className="stat-num">{fixed.length}</span><span className="stat-label">Fixed</span></div>}
            {status !== 'open' && <div className="stat"><span className="stat-num">{avgFix ?? '–'}</span><span className="stat-label">Average days to fix</span></div>}
          </div>
        )}
        {error && <p className="notice-line notice-bad">{error}</p>}
      </section>

      <section className="card">
        {!list && !error && <p className="muted">Loading…</p>}
        {list && !shown.length && <p className="muted">{list.length ? 'Nothing matches these filters.' : status === 'open' ? 'No open defects.' : 'No defects yet.'}</p>}
        {shown.length > 0 && (
          <div className="table-scroll">
            <table className="table clickable defects-table">
              <thead>
                <tr><th>Job</th><th>What&apos;s wrong</th><th>Reported</th><th className="num">Days</th><th>Return visit</th><th>Status</th></tr>
              </thead>
              <tbody>
                {visible.map((d) => {
                  const days = daysBetween(d.reportedOn, d.status === 'fixed' ? d.fixedOn : today);
                  return (
                    <tr key={d.id} tabIndex={0} onClick={() => openBooking(d.bookingId)} onKeyDown={(e) => { if (e.key === 'Enter') openBooking(d.bookingId); }}>
                      <td data-label="Job">
                        <strong>{d.job.reference}</strong>
                        <div className="small">{d.job.address}, {d.job.postcode}</div>
                        <div className="small muted">{[d.job.clientRef, d.job.teamName, d.job.installedOn && `installed ${formatDay(d.job.installedOn)}`].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td data-label="What's wrong" className="wrap">{d.description}{d.status === 'fixed' && d.fixNote && <div className="small muted">Fixed: {d.fixNote}</div>}</td>
                      <td data-label="Reported">{formatDay(d.reportedOn)}<div className="small muted">{REPORTED_BY[d.reportedBy] || d.reportedBy} · raised by {d.raisedBy}</div></td>
                      <td data-label="Days" className={`num${d.status === 'open' && days > 7 ? ' warn-text' : ''}`}>{days}</td>
                      <td data-label="Return visit">
                        {d.returnReference
                          ? <>{d.returnReference}{d.returnVisit?.date
                            ? <div className="small muted">{formatDay(d.returnVisit.date)}{d.returnVisit.teamName ? ` · ${d.returnVisit.teamName}` : ''} · {STATUS_LABEL[d.returnVisit.status]}</div>
                            : <div className="small warn-text">Waiting for a date</div>}</>
                          : <span className="muted">None</span>}
                      </td>
                      <td data-label="Status">
                        <span className={`tag defect-tag d-${d.status}`}>{d.status === 'open' ? 'Open' : 'Fixed'}</span>
                        {d.status === 'fixed' && <div className="small muted">{formatDay(d.fixedOn)}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pager total={shown.length} page={onPage} size={size} onPage={setPage} onSize={setSize} label="defects" />
      </section>

      {adding && <AddDefect onClose={() => setAdding(false)} onDone={(text) => { setAdding(false); setNotice({ tone: 'ok', text }); load(); }} />}
      {modal}
    </div>
  );
}
